// src/components/lesson/diagrams/Cone.tsx
//
// Right circular cone for SAT Volume questions: V = (1/3)πr²h. This formula
// IS on the Digital SAT reference sheet, so cone figures are in-scope (the bank
// previously had no way to render them). Matches Cylinder's aesthetic: apex at
// top, a base ellipse (front half solid, back half dashed), two slant edges,
// and a dashed height to the base center.
//
// Params (all optional):
//   radius?: number, height?: number   // proportional
//   radiusLabel?: string ("r"/"3"), heightLabel?: string, slantLabel?: string
//   showHiddenEdges?: boolean (default true)
//   label?: string

type Params = {
  radius?: number
  height?: number
  radiusLabel?: string
  heightLabel?: string
  slantLabel?: string
  showHiddenEdges?: boolean
  label?: string
}

const W = 360
const H = 240
const PAD = 30
const SQUISH = 0.3
const stroke = 'rgb(34 211 238)'
const fill = 'rgb(34 211 238 / 0.08)'
const labelColor = 'rgb(251 191 36)'
const strokeW = 1.8

export default function Cone({ params }: { params: Record<string, unknown> }) {
  const p = params as Params
  const radius = typeof p.radius === 'number' && p.radius > 0 ? p.radius : 2
  const height = typeof p.height === 'number' && p.height > 0 ? p.height : 4
  const showHidden = p.showHiddenEdges !== false
  const radiusLabel = p.radiusLabel ?? 'r'
  const heightLabel = p.heightLabel ?? 'h'

  const usableH = H - 2 * PAD
  const usableW = W - 2 * PAD
  const targetRxByH = (usableH * radius) / (height + SQUISH * radius)
  const rx = Math.max(20, Math.min(targetRxByH, usableW / 2))
  const ry = rx * SQUISH
  const pixHeight = (rx * height) / radius

  const cx = W / 2
  const apexY = (H - pixHeight - ry) / 2
  const baseCy = apexY + pixHeight
  const apex = { x: cx, y: apexY }
  const baseL = { x: cx - rx, y: baseCy }
  const baseR = { x: cx + rx, y: baseCy }

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      role="img"
      aria-label={p.label ?? 'Cone'}
      className="my-3 w-full max-w-md rounded-md bg-card/60 ring-1 ring-slate-800"
    >
      {/* Filled cone body (apex + base diameter triangle) */}
      <polygon
        points={`${apex.x},${apex.y} ${baseL.x},${baseL.y} ${baseR.x},${baseR.y}`}
        fill={fill}
        stroke="none"
      />

      {/* Base ellipse — front half solid, back half dashed */}
      <path
        d={`M ${baseL.x} ${baseL.y} A ${rx} ${ry} 0 0 0 ${baseR.x} ${baseR.y}`}
        fill="none"
        stroke={stroke}
        strokeWidth={strokeW}
      />
      {showHidden && (
        <path
          d={`M ${baseL.x} ${baseL.y} A ${rx} ${ry} 0 0 1 ${baseR.x} ${baseR.y}`}
          fill="none"
          stroke={stroke}
          strokeWidth={strokeW * 0.7}
          strokeDasharray="4 3"
        />
      )}

      {/* Slant edges */}
      <line
        x1={apex.x}
        y1={apex.y}
        x2={baseL.x}
        y2={baseL.y}
        stroke={stroke}
        strokeWidth={strokeW}
      />
      <line
        x1={apex.x}
        y1={apex.y}
        x2={baseR.x}
        y2={baseR.y}
        stroke={stroke}
        strokeWidth={strokeW}
      />

      {/* Height — dashed vertical from apex to base center */}
      <line
        x1={apex.x}
        y1={apex.y}
        x2={cx}
        y2={baseCy}
        stroke={labelColor}
        strokeWidth={strokeW * 0.7}
        strokeDasharray="4 3"
      />
      <text x={cx + 6} y={(apexY + baseCy) / 2} fontSize="12" fontWeight="600" fill={labelColor}>
        {heightLabel}
      </text>

      {/* Radius on the base */}
      <circle cx={cx} cy={baseCy} r="2" fill={labelColor} />
      <line
        x1={cx}
        y1={baseCy}
        x2={baseR.x}
        y2={baseCy}
        stroke={labelColor}
        strokeWidth={strokeW * 0.8}
      />
      <text
        x={cx + rx / 2}
        y={baseCy + 16}
        fontSize="12"
        fontWeight="600"
        textAnchor="middle"
        fill={labelColor}
      >
        {radiusLabel}
      </text>

      {/* Optional slant-height label on the right slant edge */}
      {p.slantLabel && (
        <text
          x={(apex.x + baseR.x) / 2 + 6}
          y={(apex.y + baseR.y) / 2}
          fontSize="12"
          fontWeight="600"
          fill={stroke}
        >
          {p.slantLabel}
        </text>
      )}
    </svg>
  )
}
